import { useState, useRef, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { CheckIcon, ChevronsUpDownIcon, SearchIcon, XIcon } from "lucide-react"
import { cn } from "@/lib/utils"

export interface MultiselectOption {
  value: string
  label: string
  sublabel?: string
}

interface Props {
  options: MultiselectOption[]
  values: string[]
  onValuesChange: (values: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyLabel?: string
  disabled?: boolean
  /** Max width of the trigger chip list before it truncates with "+N" */
  className?: string
}

export function Multiselect({
  options,
  values,
  onValuesChange,
  placeholder = "Seleccionar...",
  searchPlaceholder = "Buscar...",
  emptyLabel = "Sin resultados",
  disabled = false,
  className,
}: Props) {
  const [open, setOpen]       = useState(false)
  const [search, setSearch]   = useState("")
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 })
  const triggerRef            = useRef<HTMLButtonElement>(null)
  const inputRef              = useRef<HTMLInputElement>(null)

  const calcPos = useCallback(() => {
    if (!triggerRef.current) return
    const r = triggerRef.current.getBoundingClientRect()
    setDropPos({ top: r.bottom + 4, left: r.left, width: Math.max(r.width, 220) })
  }, [])

  useEffect(() => {
    if (!open) return
    calcPos()

    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      const portal = document.getElementById("multiselect-portal-active")
      if (portal?.contains(target)) return
      setOpen(false)
      setSearch("")
    }

    function handleReposition() { calcPos() }

    document.addEventListener("mousedown", handleClick)
    window.addEventListener("scroll", handleReposition, true)
    window.addEventListener("resize", handleReposition)
    return () => {
      document.removeEventListener("mousedown", handleClick)
      window.removeEventListener("scroll", handleReposition, true)
      window.removeEventListener("resize", handleReposition)
    }
  }, [open, calcPos])

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10)
  }, [open])

  const filtered = options.filter(o => {
    const term = search.toLowerCase()
    return (
      o.label.toLowerCase().includes(term) ||
      (o.sublabel ?? "").toLowerCase().includes(term)
    )
  })

  function handleToggle() {
    if (disabled) return
    if (!open) calcPos()
    setOpen(v => !v)
    setSearch("")
  }

  function handleSelect(val: string) {
    if (values.includes(val)) {
      onValuesChange(values.filter(v => v !== val))
    } else {
      onValuesChange([...values, val])
    }
    // Keep the dropdown open for multi-selection
  }

  function handleRemoveChip(val: string, e: React.MouseEvent) {
    e.stopPropagation()
    onValuesChange(values.filter(v => v !== val))
  }

  const selectedOptions = values
    .map(v => options.find(o => o.value === v))
    .filter(Boolean) as MultiselectOption[]

  // Show up to 2 chips inline, then "+N más"
  const MAX_CHIPS = 2
  const visibleChips  = selectedOptions.slice(0, MAX_CHIPS)
  const overflowCount = selectedOptions.length - MAX_CHIPS

  const dropdown = open ? (
    <div
      id="multiselect-portal-active"
      style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999 }}
      className="rounded-md border bg-popover text-popover-foreground shadow-md"
    >
      {/* Search */}
      <div className="flex items-center border-b px-3">
        <SearchIcon className="size-3.5 shrink-0 text-muted-foreground mr-2" />
        <input
          ref={inputRef}
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="flex h-9 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground"
        />
        {search && (
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); setSearch("") }}
            className="text-muted-foreground hover:text-foreground"
          >
            <XIcon className="size-3.5" />
          </button>
        )}
      </div>

      {/* Options */}
      <div className="max-h-60 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">{emptyLabel}</div>
        ) : (
          filtered.map(o => {
            const selected = values.includes(o.value)
            return (
              <button
                key={o.value}
                type="button"
                onMouseDown={e => { e.preventDefault(); handleSelect(o.value) }}
                className={cn(
                  "flex w-full items-center gap-2 px-3 py-1.5 text-sm cursor-default",
                  "hover:bg-accent hover:text-accent-foreground",
                  selected && "bg-accent/40"
                )}
              >
                <CheckIcon className={cn("size-3.5 shrink-0 text-primary transition-opacity", selected ? "opacity-100" : "opacity-0")} />
                <span className="flex-1 text-left leading-tight">
                  <span>{o.label}</span>
                  {o.sublabel && (
                    <span className="ml-1.5 text-xs text-muted-foreground">{o.sublabel}</span>
                  )}
                </span>
              </button>
            )
          })
        )}
      </div>

      {/* Footer: clear selection */}
      {values.length > 0 && (
        <div className="border-t px-3 py-1.5">
          <button
            type="button"
            onMouseDown={e => { e.preventDefault(); onValuesChange([]) }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Limpiar selección
          </button>
        </div>
      )}
    </div>
  ) : null

  return (
    <div className={cn("relative", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={cn(
          "flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-1.5 text-sm",
          "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          open && "ring-2 ring-ring ring-offset-2"
        )}
      >
        <span className="flex items-center gap-1 flex-wrap min-w-0 flex-1 overflow-hidden">
          {selectedOptions.length === 0 ? (
            <span className="text-muted-foreground truncate">{placeholder}</span>
          ) : (
            <>
              {visibleChips.map(o => (
                <span
                  key={o.value}
                  className="inline-flex items-center gap-0.5 rounded bg-primary/10 text-primary text-xs px-1.5 py-0.5 font-medium max-w-[120px]"
                >
                  <span className="truncate">{o.label}</span>
                  <span
                    role="button"
                    tabIndex={-1}
                    onMouseDown={e => handleRemoveChip(o.value, e)}
                    className="shrink-0 hover:text-primary/60 cursor-pointer"
                  >
                    <XIcon className="size-2.5" />
                  </span>
                </span>
              ))}
              {overflowCount > 0 && (
                <span className="text-xs text-muted-foreground shrink-0">+{overflowCount} más</span>
              )}
            </>
          )}
        </span>
        <ChevronsUpDownIcon className="size-4 shrink-0 opacity-50 ml-2" />
      </button>

      {typeof document !== "undefined" && createPortal(dropdown, document.body)}
    </div>
  )
}
